/**
 * PalladiumSwiper — Swiper.js Initializer
 *
 * Reads data-pb-* attributes from .swiper containers and initializes
 * Swiper instances with full parameter support: effects, interaction,
 * thumbs gallery, zoom, parallax, grid, free mode, and more.
 *
 * Uses MutationObserver to detect Blazor Server interactive rendering,
 * since DOMContentLoaded fires before SignalR delivers the actual content.
 */
(function () {
    'use strict';

    // Self-hosted (vendored swiper@11) — no cross-origin request for ad blockers to strip.
    var SWIPER_JS_CDN = '/_content/Palladium.PageBuilder/js/lib/swiper/swiper-bundle.min.js';
    var SWIPER_CSS_CDN = '/_content/Palladium.PageBuilder/js/lib/swiper/swiper-bundle.min.css';

    var instances = {};
    var thumbInstances = {};
    var _observer = null;
    var _pendingInit = null;

    // ── Helpers ──────────────────────────────────────────────────────────────

    // Shared loader (pd-load.js, loaded before this file).
    function loadCSS(url) { PdLoad.css(url); }

    function loadScript(url) { return PdLoad.script(url, function () { return !!window.Swiper; }); }

    // Dual-read helpers: try data-pb-{name} first, then direct {name} attribute.
    // This supports both legacy (data-pb-*) and ContentType (direct) formats.

    function rawAttr(el, name) {
        var val = el.getAttribute('data-pb-' + name);
        if (val !== null) return val;
        return el.getAttribute(name);
    }

    function readAttr(el, name, fallback) {
        var val = rawAttr(el, name);
        return val !== null ? val : fallback;
    }

    function readBool(el, name, fallback) {
        var val = rawAttr(el, name);
        if (val === null) return fallback;
        return val === 'true' || val === '1' || val === 'True';
    }

    function readInt(el, name, fallback) {
        var val = rawAttr(el, name);
        if (val === null) return fallback;
        var n = parseInt(val, 10);
        return isNaN(n) ? fallback : n;
    }

    function readFloat(el, name, fallback) {
        var val = rawAttr(el, name);
        if (val === null) return fallback;
        var n = parseFloat(val);
        return isNaN(n) ? fallback : n;
    }

    // ── Creative effect presets ──────────────────────────────────────────────

    var CREATIVE_PRESETS = {
        scale: {
            prev: { translate: ['-120%', 0, -500], scale: 0.8 },
            next: { translate: ['120%', 0, -500], scale: 0.8 },
        },
        rotate: {
            prev: { translate: ['-125%', 0, -800], rotate: [0, 0, -90] },
            next: { translate: ['125%', 0, -800], rotate: [0, 0, 90] },
        },
        parallax: {
            prev: { translate: ['-20%', 0, -1], shadow: true },
            next: { translate: ['100%', 0, 0] },
        },
        stack: {
            prev: { scale: 0.6, opacity: 0.5, translate: [0, 0, -400] },
            next: { translate: ['100%', 0, 0] },
        },
        shutters: {
            prev: { translate: ['-100%', 0, 0], rotate: [0, 0, -5], opacity: 0 },
            next: { translate: ['100%', 0, 0], rotate: [0, 0, 5], opacity: 0 },
        },
    };

    // ── Ensure UI elements exist ────────────────────────────────────────────

    function ensureElement(parent, className) {
        var el = parent.querySelector('.' + className);
        if (!el) {
            el = document.createElement('div');
            el.className = className;
            parent.appendChild(el);
        }
        return el;
    }

    // ── Initialize All ──────────────────────────────────────────────────────

    async function initAll() {
        var containers = document.querySelectorAll('.swiper[data-pb-effect], .swiper[effect]');
        if (containers.length === 0) return;

        loadCSS(SWIPER_CSS_CDN);
        await loadScript(SWIPER_JS_CDN);

        containers.forEach(function (el) {
            if (el._swiperInitialized) return;
            // Skip thumbs containers (initialized by their parent)
            if (el.classList.contains('swiper-thumbs')) return;
            initOne(el);
        });
    }

    // ── Initialize One Slider ───────────────────────────────────────────────

    function initOne(el) {
        var id = el.id;
        if (!id) {
            id = 'swiper-' + Math.random().toString(36).substr(2, 8);
            el.id = id;
        }

        // Destroy previous instances
        if (instances[id]) {
            instances[id].destroy(true, true);
            delete instances[id];
        }
        if (thumbInstances[id]) {
            thumbInstances[id].destroy(true, true);
            delete thumbInstances[id];
        }

        // ── Read all attributes ──

        // Core
        var effect    = readAttr(el, 'effect', 'slide');
        var speed     = readInt(el, 'speed', 500);
        var direction = readAttr(el, 'direction', 'horizontal');

        // Autoplay
        var autoplay        = readBool(el, 'autoplay', true);
        var autoplayDelay   = readInt(el, 'autoplay-delay', 5000);
        var pauseHover      = readBool(el, 'pause-hover', true);
        var autoplayReverse = readBool(el, 'autoplay-reverse', false);
        var autoplayStopLast = readBool(el, 'autoplay-stop-last', false);

        // Loop
        var loop   = readBool(el, 'loop', true);
        var rewind = readBool(el, 'rewind', false);

        // Pagination
        var showPagination    = readBool(el, 'show-pagination', true);
        var paginationType    = readAttr(el, 'pagination-type', 'bullets');
        var paginationDynamic = readBool(el, 'pagination-dynamic', false);
        var paginationClickable = readBool(el, 'pagination-clickable', true);

        // Navigation
        var showNavigation = readBool(el, 'show-navigation', true);

        // Scrollbar
        var showScrollbar     = readBool(el, 'show-scrollbar', false);
        var scrollbarDraggable = readBool(el, 'scrollbar-draggable', true);
        var scrollbarHide     = readBool(el, 'scrollbar-hide', true);

        // Multi-slides (desktop values)
        var slidesPerView  = readInt(el, 'slides-per-view', 1);
        var slidesPerGroup = readInt(el, 'slides-per-group', 1);
        var spaceBetween   = readInt(el, 'space-between', 0);
        var centered       = readBool(el, 'centered', false);
        var autoHeight     = readBool(el, 'auto-height', false);
        var gridRows       = readInt(el, 'grid-rows', 1);
        var gridFill       = readAttr(el, 'grid-fill', 'column');

        // Interaction
        var grabCursor     = readBool(el, 'grab-cursor', true);
        var keyboard       = readBool(el, 'keyboard', false);
        var mousewheel     = readBool(el, 'mousewheel', false);
        var freeMode       = readBool(el, 'free-mode', false);
        var freeModeSticky = readBool(el, 'free-mode-sticky', false);
        var slideToClicked = readBool(el, 'slide-to-clicked', false);

        // Effect params
        var fadeCrossfade     = readBool(el, 'fade-crossfade', true);
        var coverflowRotate   = readFloat(el, 'coverflow-rotate', 30);
        var coverflowDepth    = readInt(el, 'coverflow-depth', 100);
        var coverflowStretch  = readInt(el, 'coverflow-stretch', 0);
        var coverflowModifier = readFloat(el, 'coverflow-modifier', 1);
        var coverflowScale    = readFloat(el, 'coverflow-scale', 1);
        var coverflowShadows  = readBool(el, 'coverflow-shadows', true);
        var cubeShadow        = readBool(el, 'cube-shadow', true);
        var cubeSlideShadows  = readBool(el, 'cube-slide-shadows', true);
        var flipShadows       = readBool(el, 'flip-shadows', true);
        var cardsOffset       = readInt(el, 'cards-offset', 8);
        var cardsRotate       = readFloat(el, 'cards-rotate', 2);
        var creativePreset    = readAttr(el, 'creative-preset', 'scale');

        // Thumbs
        var showThumbs    = readBool(el, 'show-thumbs', false);
        var thumbsPerView = readInt(el, 'thumbs-per-view', 4);
        var thumbsSpace   = readInt(el, 'thumbs-space', 10);

        // Advanced
        var parallax    = readBool(el, 'parallax', false);
        var zoom        = readBool(el, 'zoom', false);
        var zoomMax     = readFloat(el, 'zoom-max', 3);
        var lazyPreload = readInt(el, 'lazy-preload', 1);
        var cssMode     = readBool(el, 'css-mode', false);

        // Responsive suffixed values (dual-read: data-pb-* or direct)
        var slidesPerViewMd  = readInt(el, 'slides-per-view-md', 0);
        var spaceBetweenMd   = readInt(el, 'space-between-md', 0);
        var centeredMd       = rawAttr(el, 'centered-md');
        var slidesPerGroupMd = readInt(el, 'slides-per-group-md', 0);

        var slidesPerViewSm  = readInt(el, 'slides-per-view-sm', 0);
        var spaceBetweenSm   = readInt(el, 'space-between-sm', 0);
        var centeredSm       = rawAttr(el, 'centered-sm');
        var slidesPerGroupSm = readInt(el, 'slides-per-group-sm', 0);

        // ── Effects that only support slidesPerView: 1 ──
        var singleSlideEffects = ['fade', 'cube', 'flip', 'cards', 'creative'];
        var isCreative = effect.indexOf('creative') === 0;
        var actualEffect = isCreative ? 'creative' : effect;
        var forceSingle = singleSlideEffects.indexOf(actualEffect) !== -1;

        // ── Build config ──

        var config = {
            effect: actualEffect,
            speed: speed,
            direction: direction,
            slidesPerView: forceSingle ? 1 : slidesPerView,
            spaceBetween: forceSingle ? 0 : spaceBetween,
            centeredSlides: forceSingle ? false : centered,
            slidesPerGroup: forceSingle ? 1 : slidesPerGroup,
            grabCursor: grabCursor,
            autoHeight: autoHeight,
            rewind: !loop && rewind,
            loop: loop,
            cssMode: cssMode,
            slideToClickedSlide: slideToClicked,
            watchSlidesProgress: showThumbs,
            lazyPreloadPrevNext: lazyPreload,
        };

        // ── Grid ──
        if (!forceSingle && gridRows > 1) {
            config.grid = {
                rows: gridRows,
                fill: gridFill,
            };
        }

        // ── Responsive breakpoints ──
        if (!forceSingle) {
            var breakpoints = {};
            var hasBreakpoints = false;

            // Mobile (< 576px)
            if (slidesPerViewSm > 0 || spaceBetweenSm > 0 || centeredSm !== null || slidesPerGroupSm > 0) {
                breakpoints[0] = {
                    slidesPerView:  slidesPerViewSm > 0  ? slidesPerViewSm  : slidesPerView,
                    spaceBetween:   spaceBetweenSm > 0   ? spaceBetweenSm   : spaceBetween,
                    centeredSlides: centeredSm !== null   ? (centeredSm === 'true') : centered,
                    slidesPerGroup: slidesPerGroupSm > 0  ? slidesPerGroupSm  : slidesPerGroup,
                };
                hasBreakpoints = true;
            }

            // Tablet (>= 576px)
            if (slidesPerViewMd > 0 || spaceBetweenMd > 0 || centeredMd !== null || slidesPerGroupMd > 0) {
                breakpoints[576] = {
                    slidesPerView:  slidesPerViewMd > 0  ? slidesPerViewMd  : slidesPerView,
                    spaceBetween:   spaceBetweenMd > 0   ? spaceBetweenMd   : spaceBetween,
                    centeredSlides: centeredMd !== null   ? (centeredMd === 'true') : centered,
                    slidesPerGroup: slidesPerGroupMd > 0  ? slidesPerGroupMd  : slidesPerGroup,
                };
                hasBreakpoints = true;
            }

            // Desktop (>= 992px)
            if (hasBreakpoints) {
                breakpoints[992] = {
                    slidesPerView: slidesPerView,
                    spaceBetween: spaceBetween,
                    centeredSlides: centered,
                    slidesPerGroup: slidesPerGroup,
                };
                config.slidesPerView  = breakpoints[0] ? breakpoints[0].slidesPerView  : slidesPerView;
                config.spaceBetween   = breakpoints[0] ? breakpoints[0].spaceBetween   : spaceBetween;
                config.centeredSlides = breakpoints[0] ? breakpoints[0].centeredSlides  : centered;
                config.slidesPerGroup = breakpoints[0] ? breakpoints[0].slidesPerGroup  : slidesPerGroup;
                config.breakpoints = breakpoints;
            }
        }

        // ── Effect-specific params ──
        if (actualEffect === 'fade') {
            config.fadeEffect = { crossFade: fadeCrossfade };
        } else if (actualEffect === 'cube') {
            config.cubeEffect = { shadow: cubeShadow, slideShadows: cubeSlideShadows };
        } else if (actualEffect === 'coverflow') {
            config.coverflowEffect = {
                rotate: coverflowRotate,
                stretch: coverflowStretch,
                depth: coverflowDepth,
                modifier: coverflowModifier,
                scale: coverflowScale,
                slideShadows: coverflowShadows,
            };
        } else if (actualEffect === 'flip') {
            config.flipEffect = { slideShadows: flipShadows };
        } else if (actualEffect === 'cards') {
            config.cardsEffect = {
                perSlideOffset: cardsOffset,
                perSlideRotate: cardsRotate,
            };
        } else if (actualEffect === 'creative') {
            var preset = CREATIVE_PRESETS[creativePreset] || CREATIVE_PRESETS.scale;
            config.creativeEffect = {
                prev: preset.prev,
                next: preset.next,
                perspective: true,
            };
        }

        // ── Autoplay ──
        if (autoplay) {
            config.autoplay = {
                delay: autoplayDelay,
                disableOnInteraction: false,
                pauseOnMouseEnter: pauseHover,
                reverseDirection: autoplayReverse,
                stopOnLastSlide: autoplayStopLast,
            };
        }

        // ── Keyboard ──
        if (keyboard) {
            config.keyboard = { enabled: true, onlyInViewport: true };
        }

        // ── Mousewheel ──
        if (mousewheel) {
            config.mousewheel = { enabled: true, forceToAxis: true };
        }

        // ── Free Mode ──
        if (freeMode) {
            config.freeMode = {
                enabled: true,
                sticky: freeModeSticky,
                momentum: true,
                momentumBounce: true,
            };
        }

        // ── Parallax ──
        if (parallax) {
            config.parallax = { enabled: true };
        }

        // ── Zoom ──
        if (zoom) {
            config.zoom = {
                maxRatio: zoomMax,
                minRatio: 1,
                toggle: true,
            };
        }

        // ── Pagination ──
        if (showPagination) {
            var paginationEl = ensureElement(el, 'swiper-pagination');
            config.pagination = {
                el: paginationEl,
                type: paginationType,
                clickable: paginationClickable,
                dynamicBullets: paginationDynamic,
            };
        }

        // ── Navigation ──
        if (showNavigation) {
            var prevEl = ensureElement(el, 'swiper-button-prev');
            var nextEl = ensureElement(el, 'swiper-button-next');
            config.navigation = {
                prevEl: prevEl,
                nextEl: nextEl,
            };
        }

        // ── Scrollbar ──
        if (showScrollbar) {
            var scrollbarEl = ensureElement(el, 'swiper-scrollbar');
            config.scrollbar = {
                el: scrollbarEl,
                draggable: scrollbarDraggable,
                hide: scrollbarHide,
            };
        }

        // ── Thumbs Gallery ──
        var thumbsSwiper = null;
        if (showThumbs) {
            thumbsSwiper = initThumbs(el, id, thumbsPerView, thumbsSpace, speed);
        } else {
            // Remove leftover thumbs container if thumbs disabled
            var oldThumbs = document.getElementById(id + '-thumbs');
            if (oldThumbs) oldThumbs.remove();
        }

        if (thumbsSwiper) {
            config.thumbs = { swiper: thumbsSwiper };
        }

        // ── Create main Swiper ──
        instances[id] = new Swiper(el, config);
        el._swiperInitialized = true;
        el.setAttribute('data-pd-ready', '');
    }

    // ── Thumbs Gallery Initializer ──────────────────────────────────────────

    function initThumbs(mainEl, mainId, perView, space, speed) {
        var thumbsId = mainId + '-thumbs';
        var thumbsEl = document.getElementById(thumbsId);

        // If Blazor already rendered the thumbs container, use it
        if (!thumbsEl) {
            // Auto-generate thumbs from main slides
            thumbsEl = document.createElement('div');
            thumbsEl.id = thumbsId;
            thumbsEl.className = 'swiper swiper-thumbs';
            thumbsEl.style.marginTop = '10px';

            var wrapper = document.createElement('div');
            wrapper.className = 'swiper-wrapper';

            // Clone slide images as thumbnails
            var mainSlides = mainEl.querySelectorAll('.swiper-slide');
            mainSlides.forEach(function (slide) {
                var thumb = document.createElement('div');
                thumb.className = 'swiper-slide';
                thumb.style.cursor = 'pointer';
                thumb.style.opacity = '0.6';
                thumb.style.transition = 'opacity 0.3s';

                var img = slide.querySelector('img');
                if (img) {
                    var thumbImg = document.createElement('img');
                    thumbImg.src = img.src;
                    thumbImg.alt = img.alt || 'Thumbnail';
                    thumbImg.loading = 'lazy';
                    thumbImg.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block';
                    thumb.appendChild(thumbImg);
                }

                wrapper.appendChild(thumb);
            });

            thumbsEl.appendChild(wrapper);
            mainEl.parentNode.insertBefore(thumbsEl, mainEl.nextSibling);
        }

        // Initialize thumbs swiper
        var thumbsConfig = {
            slidesPerView: perView,
            spaceBetween: space,
            speed: speed,
            watchSlidesProgress: true,
            freeMode: { enabled: true, sticky: false },
            on: {
                slideChangeTransitionStart: function () {
                    // Highlight active thumb
                    var slides = thumbsEl.querySelectorAll('.swiper-slide');
                    slides.forEach(function (s) { s.style.opacity = '0.6'; });
                },
                slideChangeTransitionEnd: function () {
                    var active = thumbsEl.querySelector('.swiper-slide-thumb-active');
                    if (active) active.style.opacity = '1';
                },
            },
        };

        var ts = new Swiper(thumbsEl, thumbsConfig);
        thumbInstances[mainId] = ts;
        thumbsEl._swiperInitialized = true;

        return ts;
    }

    // ── Destroy helpers ─────────────────────────────────────────────────────

    function destroy(id) {
        if (thumbInstances[id]) {
            thumbInstances[id].destroy(true, true);
            delete thumbInstances[id];
        }
        if (instances[id]) {
            instances[id].destroy(true, true);
            delete instances[id];
        }
        var el = document.getElementById(id);
        if (el) el._swiperInitialized = false;
    }

    function destroyAll() {
        Object.keys(instances).forEach(function (id) {
            destroy(id);
        });
    }

    // ── MutationObserver: detect when Blazor renders .swiper elements ──────

    function startObserver() {
        if (_observer) return;

        _observer = new MutationObserver(function () {
            if (_pendingInit) clearTimeout(_pendingInit);
            _pendingInit = setTimeout(function () {
                _pendingInit = null;
                var uninitialized = document.querySelectorAll('.swiper[data-pb-effect]:not([data-swiper-ready])');
                if (uninitialized.length > 0) initAll();
            }, 150);
        });

        _observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    // ── Public API ──────────────────────────────────────────────────────────

    window.PalladiumSwiper = {
        initAll: initAll,
        initOne: initOne,
        destroy: destroy,
        destroyAll: destroyAll,
    };

    // Start observing immediately
    if (document.body) {
        startObserver();
        initAll();
    } else {
        document.addEventListener('DOMContentLoaded', function () {
            startObserver();
            initAll();
        });
    }

    // ═══ VARIANT IMAGE CHANGE LISTENER ═══
    // Listens for 'variant-images-changed' event and updates
    // any Swiper that has data-pb-media-field attribute.
    window.addEventListener('variant-images-changed', function (e) {
        var images = e.detail && e.detail.images;
        if (!images || !images.length) return;

        // Find swipers with media-field (product gallery sliders)
        var targets = document.querySelectorAll('.swiper[data-pb-media-field]');

        // Fallback: first swiper with thumbs
        if (targets.length === 0) {
            targets = document.querySelectorAll('.swiper[data-pb-show-thumbs]');
        }

        targets.forEach(function (el) {
            var sw = el.swiper || instances[el.id];
            if (!sw) return;

            // Update main slides
            sw.removeAllSlides();
            images.forEach(function (url) {
                sw.appendSlide(
                    '<div class="swiper-slide" style="position:relative;overflow:hidden">' +
                    '<img src="' + url + '" alt="" loading="lazy" ' +
                    'style="width:100%;height:100%;object-fit:contain;display:block" />' +
                    '</div>'
                );
            });
            sw.slideTo(0, 0);
            sw.update();

            // Update thumbs gallery if exists
            var thumbsEl = document.getElementById(el.id + '-thumbs');
            var thumbsSw = thumbsEl && (thumbsEl.swiper || thumbInstances[thumbsEl.id]);
            if (thumbsSw) {
                thumbsSw.removeAllSlides();
                images.forEach(function (url) {
                    thumbsSw.appendSlide(
                        '<div class="swiper-slide" style="cursor:pointer;opacity:0.6;transition:opacity 0.3s">' +
                        '<img src="' + url + '" alt="" loading="lazy" ' +
                        'style="width:100%;height:100%;object-fit:cover;display:block" />' +
                        '</div>'
                    );
                });
                thumbsSw.slideTo(0, 0);
                thumbsSw.update();
            }
        });
    });
})();
